// frontend/src/app/pages/dashboard/Apps/ProductAnalysisWrapper.tsx

import React, { useEffect, useState, useRef } from 'react'
import { io, Socket } from 'socket.io-client'
import { Toast } from 'primereact/toast'
import { DataTable, DataTableExpandedRows } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Tag } from 'primereact/tag'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Checkbox } from 'primereact/checkbox'
import * as XLSX from 'xlsx'
import { Table } from 'react-bootstrap' // you used for job details

import { API_URL } from '../../../modules/auth/core/_requests'
import './productAnalysisWrapper.css'
import BrandScoresChart from './serviceComponents/BrandScoresChart'

// ===================== Types =====================
interface JobRecord {
  job_id: string
  status: string
  started_at: string | null
  completed_at: string | null
}

interface DifferencesInfo {
  raw: any
  processed: any
  staging?: any
  percentageDifference: number
}

interface ComparisonRow {
  upc: string
  brand: string
  raw?: {
    completenessScore?: number
    [key: string]: any
  }
  staging?: {
    completenessScore?: number
    [key: string]: any
  }
  processed?: {
    completenessScore?: number
    differences?: Record<string, DifferencesInfo>
    [key: string]: any
  }
  differences?: Record<string, DifferencesInfo>
  completeness_score?: number
}

interface BrandScore {
  brand: string
  avgScore: number
}

/** For tracking progress steps */
interface ProgressStep {
  id: string
  text: string
  status: 'pending' | 'completed' | 'error'
}

// ===================== Utilities =====================

/** Color scale for difference background */
function computeDifferenceColor(pct: number) {
  const alpha = Math.min(1, pct / 100) * 0.3
  return `rgba(255, 0, 0, ${alpha})`
}

/** Render completeness score with a colored <Tag> */
function scoreBodyTemplate(score: number) {
  let severity: 'success' | 'warning' | 'danger' = 'success'
  if (score < 50) severity = 'danger'
  else if (score < 80) severity = 'warning'
  return <Tag value={score} severity={severity} />
}

// ===================== Component =====================
const ProductAnalysisWrapper: React.FC = () => {
  // 1) Job list & selected job
  const [jobs, setJobs] = useState<JobRecord[]>([])
  const [selectedJob, setSelectedJob] = useState<JobRecord | null>(null)

  // 2) Main chunked data & brand scores
  const [comparisonData, setComparisonData] = useState<ComparisonRow[]>([])
  const [brandScores, setBrandScores] = useState<BrandScore[]>([])

  // 3) Row expansion, filters, etc.
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({})
  const [globalFilter, setGlobalFilter] = useState('')
  const [brandFilter, setBrandFilter] = useState('')

  // 4) Use precomputed differences & show staging toggles
  const [usePrecomputed, setUsePrecomputed] = useState(false)
  const [showStaging, setShowStaging] = useState(false)

  // 5) UI states
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [btnLoading, setBtnLoading] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false) // for Recalculate
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([])

  // 6) Socket
  const [socket, setSocket] = useState<Socket | null>(null)
  const [socketId, setSocketId] = useState<string | undefined>(undefined)
  const toast = useRef<Toast>(null)

  // We will store partial data in a Map by upc to avoid duplicates
  const partialMapRef = React.useRef<Map<string, ComparisonRow>>(new Map())

  // ===================== 1) On mount: connect socket, fetch jobs
  useEffect(() => {
    const s = io(API_URL, { transports: ['websocket'] })
    setSocket(s)

    s.on('connect', () => {
      setSocketId(s.id)
      console.log('Socket connected, ID:', s.id)
    })

    // chunk partial results
    s.on('partialResults', (payload: { partialData: ComparisonRow[] }) => {
      // We unify partial data by upc in a map to avoid duplicates
      partialMapRef.current = unifyPartialData(partialMapRef.current, payload.partialData)
      // Convert map back to array
      const unifiedArray = Array.from(partialMapRef.current.values())
      setComparisonData(unifiedArray)
    })

    // chunk final
    s.on('partialResultsDone', (payload: { comparisonData: ComparisonRow[]; brandScores: BrandScore[] }) => {
      // If the final payload is the entire dataset, we can simply set it
      setComparisonData(payload.comparisonData)
      setBrandScores(payload.brandScores)
      addStep('All chunked data loaded successfully!', 'completed')
      setLoading(false)
      // Optionally clear partialMapRef since we have the final data
      partialMapRef.current.clear()
    })

    // progress
    s.on('progress', (msg: string) => {
      addStep(msg, 'pending')
    })

    // error
    s.on('error', (errMsg: string) => {
      addStep(errMsg, 'error')
      setLoading(false)
      setError(errMsg)
    })

    s.on('disconnect', () => {
      console.log('Socket disconnected')
      setSocket(null)
    })

    fetchJobs()

    return () => {
      s.disconnect()
    }
  }, [])

  /** unifyPartialData: merges new chunk data into an existing Map by upc */
  function unifyPartialData(
    existingMap: Map<string, ComparisonRow>,
    newRows: ComparisonRow[]
  ): Map<string, ComparisonRow> {
    const updatedMap = new Map(existingMap)
    for (const row of newRows) {
      updatedMap.set(row.upc, row)
    }
    return updatedMap
  }

  /** Steps helpers */
  const addStep = (text: string, status: 'pending' | 'completed' | 'error') => {
    setProgressSteps((prevSteps) => {
      const updatedSteps = [...prevSteps]

      // Mark the last pending step as completed
      const lastStep = updatedSteps[updatedSteps.length - 1]
      if (lastStep && lastStep.status === 'pending') {
        updatedSteps[updatedSteps.length - 1] = { ...lastStep, status: 'completed' }
      }
      // Add the new step
      updatedSteps.push({
        id: `${Date.now()}-${Math.random()}`,
        text,
        status,
      })
      return updatedSteps
    })
  }

  const clearSteps = () => setProgressSteps([])

  // ===================== 2) Fetch Jobs
  const fetchJobs = async () => {
    setLoading(true)
    clearSteps()
    addStep('Fetching Jobs...', 'pending')
    try {
      const resp = await fetch(`${API_URL}/api/product-info-analysis/jobs`)
      if (!resp.ok) throw new Error('Failed to fetch jobs')
      const data = await resp.json()
      setJobs(data.jobs || [])
      addStep('Jobs fetched successfully!', 'completed')
    } catch (err: any) {
      setError(err.message)
      addStep('Error fetching jobs', 'error')
    } finally {
      setLoading(false)
    }
  }

  // ===================== 3) Chunked fetch from results
  const startChunkedFetch = async () => {
    if (!socketId || !selectedJob) {
      addStep('No job selected or no socket ID found.', 'error')
      return
    }
    setLoading(true)
    clearSteps()
    // Clear any existing partial data
    partialMapRef.current.clear()
    setComparisonData([])
    setBrandScores([])

    addStep('Starting chunked retrieval', 'pending')

    const jobId = selectedJob.job_id
    const usePre = usePrecomputed ? 'true' : 'false'
    const url = `${API_URL}/api/product-info-analysis/results?job_id=${jobId}&socketId=${socketId}&chunked=true&usePrecomputed=${usePre}`

    try {
      const resp = await fetch(url)
      if (!resp.ok) {
        throw new Error('Failed to start chunked retrieval')
      }
      // We won't parse any response body from the server's immediate "OK" response
      addStep('Chunk retrieval accepted by server', 'pending')
    } catch (err: any) {
      setError(err.message)
      addStep('Error starting chunked retrieval', 'error')
      setLoading(false)
    }
  }

  // ===================== 4) Recalculate => confirm dialog
  const onConfirmRecalc = async () => {
    setShowConfirmDialog(false)
    setLoading(true)
    addStep('Starting Recalc (Python ETL)...', 'pending')
    try {
      const resp = await fetch(`${API_URL}/api/product-info-analysis/recalculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await resp.json()
      if (!resp.ok) {
        setError(data.error || 'Failed to start recalc job')
        addStep('Error starting Recalc job', 'error')
      } else {
        addStep('Recalc job started successfully.', 'completed')
      }
    } catch (err: any) {
      setError(err.message)
      addStep('Error starting Recalc job', 'error')
    } finally {
      setLoading(false)
    }
  }
  const confirmFooter = (
    <div>
      <Button label="No" icon="pi pi-times" onClick={() => setShowConfirmDialog(false)} className="p-button-text" />
      <Button label="Yes" icon="pi pi-check" onClick={onConfirmRecalc} className="p-button-danger" />
    </div>
  )

  // ===================== 5) Filters
  const uniqueBrands = Array.from(new Set(comparisonData.map((d) => d.brand))).filter(Boolean).sort()
  const filteredData = comparisonData.filter((row) => {
    if (brandFilter && row.brand !== brandFilter) return false
    if (globalFilter) {
      return JSON.stringify(row).toLowerCase().includes(globalFilter.toLowerCase())
    }
    return true
  })

  // ===================== 6) Expand row template
  const rowExpansionTemplate = (rowData: ComparisonRow) => {
    // We look in rowData.processed?.differences or rowData.differences
    const diffs = rowData.processed?.differences || rowData.differences
    if (!diffs) {
      return (
        <div style={{ padding: '1em' }}>
          <strong>No differences found for this UPC.</strong>
        </div>
      )
    }
    const fields = Object.keys(diffs)
    if (!fields.length) {
      return (
        <div style={{ padding: '1em' }}>
          <strong>No differences from RAW to PROCESSED!</strong>
        </div>
      )
    }

    // If "Show Staging" is on, we can show staging as a third column, if staging data is present
    const hasStaging = showStaging 

    return (
      <div style={{ padding: '8px' }}>
        <table className="p-datatable p-component" style={{ width: '100%' }}>
          <thead className="p-datatable-thead custom-data-table">
            <tr>
              <th>Field</th>
              <th>Raw Value</th>
              {hasStaging && <th>Staging Value</th>}
              <th>Processed Value</th>
              <th style={{ width: '120px' }}>Diff (%)</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const diff = diffs[field]
              const rawVal = diff.raw ?? ''
              const stagingVal = diff.staging ?? ''
              const processedVal = diff.processed ?? ''
              const bgColor = computeDifferenceColor(diff.percentageDifference)
              return (
                <tr key={field} style={{ backgroundColor: bgColor, padding: '5px' }}>
                  <td>{field}</td>
                  <td>{String(rawVal)}</td>
                  {hasStaging && <td>{String(stagingVal)}</td>}
                  <td>{String(processedVal)}</td>
                  <td>{diff.percentageDifference}%</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const onRowClick = (e: any) => {
    const rowData: ComparisonRow = e.data
    const key = rowData.upc
    const newExpandedRows = { ...expandedRows }
    if (newExpandedRows[key]) {
      delete newExpandedRows[key]
    } else {
      newExpandedRows[key] = true
    }
    setExpandedRows(newExpandedRows)
  }

  // ===================== 7) Export
  const exportAllDataToExcel = (compData: ComparisonRow[]) => {
    if (!compData || !compData.length) {
      alert('No data available for export.')
      return
    }
    setBtnLoading(true)

    try {
      // union of all difference fields
      const allFieldsSet = new Set<string>()
      compData.forEach((row) => {
        const diffs = row.processed?.differences || row.differences
        if (diffs) {
          Object.keys(diffs).forEach((key) => allFieldsSet.add(key))
        }
      })

      const globalFields = Array.from(allFieldsSet)
      const headers = ['upc', 'data_type', ...globalFields]
      const exportRows: Record<string, any>[] = []

      compData.forEach((row) => {
        const rawRow: Record<string, any> = { upc: row.upc, data_type: 'Raw' }
        const procRow: Record<string, any> = { upc: row.upc, data_type: 'Processed' }
        const diffs = row.processed?.differences || row.differences

        globalFields.forEach((field) => {
          if (diffs && diffs[field]) {
            rawRow[field] = diffs[field].raw ?? ''
            procRow[field] = diffs[field].processed ?? ''
          } else {
            // fallback to row.raw or empty
            rawRow[field] = row.raw?.[field] ?? ''
            procRow[field] = ''
          }
        })

        exportRows.push(rawRow)
        exportRows.push(procRow)
      })

      const worksheet = XLSX.utils.json_to_sheet(exportRows, { header: headers })
      worksheet['!cols'] = headers.map((h) => ({ wch: h.length + 5 }))
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'All Data')
      const currentDateTime = new Date().toISOString().replace(/[:.-]/g, '_')
      const fileName = `productsComparison_${currentDateTime}.xlsx`
      XLSX.writeFile(workbook, fileName)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBtnLoading(false)
    }
  }

  // ===================== Rendering =====================
  return (
    <div className="info-app-wrapper info-app-container  card text-body bg-body">
      <Toast ref={toast} position="bottom-left" />

      {/* Confirmation Dialog for Recalc */}
      <Dialog
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        header="Confirm Recalculation"
        footer={
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <Button label="No" icon="pi pi-times" onClick={() => setShowConfirmDialog(false)} className="p-button-text" />
            <Button label="Yes" icon="pi pi-check" onClick={onConfirmRecalc} className="p-button-danger" />
          </div>
        }
      >
        <p>This will initiate a Python ETL job, which may take 1-2 hours to complete. Do you wish to proceed?</p>
      </Dialog>

      {/* Top bar: Title & Recalc Fresh in top-right */}
      <div className='card-header d-flex align-items-center  pb-2 pt-2'>
        <h2 className='card-title m-0 mb-0'>Product Info Analysis</h2>
        <div className='col-auto'>
          <Button
            label="Recalculate Fresh"
            icon="pi pi-exclamation-triangle"
            className="p-button btn-primary"
            onClick={() => setShowConfirmDialog(true)}
          />
        </div>
      </div>
      
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <div className='card-body bg-body text-body mt-4'>
        {/* 1) JOB SELECTION */}
        <div className='d-flex gap-4 flex-wrap align-items-center mb-2 justify-content-between pb-4 border-bottom'>
          <div>
            <label htmlFor="jobSelect">Select a completed job:</label>
            <select
              id="jobSelect"
              style={{ width: '95%', padding: '8px', marginTop: '0.5rem' }}
              value={selectedJob?.job_id || ''}
              onChange={(e) => {
                const jobId = e.target.value
                const job = jobs.find((j) => j.job_id === jobId) || null
                setSelectedJob(job)
              }}
            >
              <option value="">-- Select a completed job --</option>
              {jobs.map((j) => (
                <option key={j.job_id} value={j.job_id}>
                  {j.completed_at
                    ? new Date(j.completed_at).toLocaleDateString('en-GB', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      }) +
                      ' ' +
                      new Date(j.completed_at).toLocaleTimeString()
                    : 'N/A'}
                </option>
              ))}
            </select>
          </div>

          {/* 2) Show job details if any */}
          {selectedJob && (
            <div>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Status</th>
                    <th>Started At</th>
                    <th>Completed At</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ cursor: 'pointer' }}>
                    <td>{selectedJob.job_id}</td>
                    <td>{selectedJob.status}</td>
                    <td>
                      {selectedJob.started_at
                        ? new Date(selectedJob.started_at).toLocaleString()
                        : 'N/A'}
                    </td>
                    <td>
                      {selectedJob.completed_at
                        ? new Date(selectedJob.completed_at).toLocaleString()
                        : 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </div>

        {/* 3) Precomputed + chunk fetch + Show Staging toggles */}
         <div className='card-header d-flex align-items-center  pb-2 pt-4' style={{borderBottom: 'unset'}}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Checkbox
              checked={showStaging}
              onChange={(e) => setShowStaging(!!e.checked)}
              inputId="showStaging"
            />
            <label htmlFor="showStaging" style={{ margin: 0 }}>
              Show Staging
            </label>
          </div>

          <Button
            label="Fetch Results (Chunked)"
            icon="pi pi-download"
            className="btn btn-info"
            onClick={startChunkedFetch}
            disabled={!selectedJob || loading}
          />
        </div>

        {/* PROGRESS STEPS */}
        {progressSteps.length > 0 && (
          <div className = "progress-steps-wrapper" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
            <ul className = "progress-steps" style={{ listStyle: 'none', paddingLeft: 0 }}>
              {progressSteps.map((step) => (
                <li key={step.id} style={{ marginTop: '0.25rem' }}>
                  {step.status === 'pending' && <i className="pi pi-spin pi-spinner" style={{ marginRight: 4 }} />}
                  {step.status === 'completed' && <i className="pi pi-check text-success" style={{ marginRight: 4 }} />}
                  {step.status === 'error' && <i className="pi pi-times text-danger" style={{ marginRight: 4 }} />}
                  <span>{step.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* If we have data, show filters & table & brand scores */}
        {comparisonData.length > 0 && (
          <>
            {/* Filters & Export */}
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '2rem',
                marginTop: '2rem',
                flexWrap: 'wrap',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <span className="p-input-icon-left" style={{ flex: '1 1 200px' }}>
                <InputText
                  placeholder="Global Search"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                />
              </span>

              <Dropdown
                value={brandFilter}
                options={['', ...uniqueBrands]}
                onChange={(e) => setBrandFilter(e.value)}
                placeholder="Filter by Brand"
                showClear
                className="table-dropdown custom-dropdown-analysis"
              />

              <Button
                label="Clear Filters"
                icon="pi pi-times"
                onClick={() => {
                  setGlobalFilter('')
                  setBrandFilter('')
                }}
                className="p-button-secondary"
              />

              <Button
                label="Export to Excel"
                icon={btnLoading ? 'pi pi-spin pi-spinner' : 'pi pi-file-excel'}
                onClick={() => exportAllDataToExcel(comparisonData)}
                disabled={btnLoading}
                className="p-button-success"
              />
            </div>

            {/* Main Table */}
            <DataTable
              value={filteredData}
              dataKey="upc"
              className="p-datatable-table main-p-datatable-table"
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
              rowExpansionTemplate={rowExpansionTemplate}
              onRowClick={(e) => {
                const rowData = e.data
                const key = rowData.upc
                const newExpandedRows = { ...expandedRows }
                if (newExpandedRows[key]) {
                  delete newExpandedRows[key]
                } else {
                  newExpandedRows[key] = true
                }
                setExpandedRows(newExpandedRows)
              }}
              paginator
              rowsPerPageOptions={[20, 50, filteredData.length]}
              rows={20}
              scrollable
              scrollHeight="80vh"
              responsiveLayout="scroll"
              stripedRows
            >
              <Column expander style={{ width: '3em' }} />
              <Column
                sortable
                field="upc"
                header="UPC"
                style={{ width: '150px' }}
              />
              <Column
                sortable
                field="brand"
                header="Brand"
                style={{ width: '150px' }}
              />

              {/* Raw completeness */}
              <Column
                header="Completeness Raw Score"
                body={(row) => scoreBodyTemplate(row.raw?.completenessScore ?? 0)}
                style={{ width: '150px' }}
              />

              {/* Processed completeness */}
              <Column
                header="Completeness Processed Score"
                body={(row) =>
                  row.processed
                    ? scoreBodyTemplate(row.processed?.completenessScore ?? 0)
                    : ''
                }
                style={{ width: '150px' }}
              />

              {/* Staging completeness if showStaging */}
              {showStaging && (
                <Column
                  header="Completeness Staging Score"
                  body={(row) =>
                    row.staging
                      ? scoreBodyTemplate(row.staging?.completenessScore ?? 0)
                      : ''
                  }
                  style={{ width: '150px' }}
                />
              )}
            </DataTable>

            {/* Brand Scores */}
            {brandScores.length > 0 && (
              <div style={{ marginTop: '4rem' }}>
                <h4>Brand Avg Score (Raw Data)</h4>
                <DataTable
                  value={brandScores}
                  className="main-p-datatable-table"
                  responsiveLayout="scroll"
                  stripedRows
                  scrollable
                  scrollHeight="80vh"
                >
                  <Column field="brand" header="Brand" style={{ width: '200px' }} />
                  <Column
                    field="avgScore"
                    header="Average Score"
                    body={(rowData: BrandScore) => scoreBodyTemplate(rowData.avgScore)}
                    style={{ width: '150px', textAlign: 'center' }}
                  />
                </DataTable>

                {/* Chart below brand scores */}
                <div style={{ marginTop: '2rem' }}>
                  <BrandScoresChart brandScores={brandScores} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ProductAnalysisWrapper
